/**
 * Which page numbers the pager prints, with `null` standing for an ellipsis.
 *
 * MUD's pagination component has no truncation logic of its own, so this is AKIS-authored
 * behaviour rather than a spec value. It exists for a measured reason: an untruncated list of page
 * numbers overflows a 320px viewport once the listing passes a handful of pages, which
 * public-site-responsive.spec.ts caught.
 *
 * The rule: always keep the first page, the last page, and a run of `2 * neighbours + 1`
 * consecutive pages around the current one; drop everything between those runs, and mark each
 * dropped run with a single gap. The run keeps its length at the ends of the listing by sliding
 * inwards rather than shrinking, so page one offers `1 2 3 … 10` and the last offers `1 … 8 9 10`
 * — the reader gets the same reach wherever they are.
 *
 * A gap that would hide exactly one page prints that page instead: the ellipsis takes the same
 * room as the number it replaces and costs the reader the click, so it buys nothing. That also
 * means the printed length does not depend on which of the two a run collapses to.
 *
 * `neighbours` is how the caller spends the width it has. One is the ordinary window; zero keeps
 * the first, current and last alone, which is the only window that fits a 320px screen — at most
 * five boxes, against nine with a run of three. Pagination picks between them; see
 * usePaginationNeighbours.
 */
export function paginationWindow(
  current: number,
  totalPages: number,
  neighbours = 1,
): (number | null)[] {
  if (totalPages <= 0) return [];

  // A set, because the runs overlap: the current page's run can reach either end of the listing.
  const keep = new Set<number>([0, totalPages - 1]);
  const run = 2 * neighbours + 1;
  const start = Math.max(0, Math.min(current - neighbours, totalPages - run));
  for (let page = start; page <= Math.min(totalPages - 1, start + run - 1); page += 1) {
    keep.add(page);
  }

  const window: (number | null)[] = [];
  let previous: number | null = null;
  for (const page of [...keep].sort((a, b) => a - b)) {
    if (previous !== null) {
      const hidden = page - previous - 1;
      if (hidden === 1) window.push(previous + 1);
      else if (hidden > 1) window.push(null);
    }
    window.push(page);
    previous = page;
  }
  return window;
}
